import { useContext, useEffect, useRef, useState } from 'react';
import { PlaycastContext } from '../../components/PlaycastWrapper';
import { useAuth, useUser } from '@clerk/clerk-react';
import { Profile } from '@playcastdotio/Messaging';
import { PlayerJoinState } from '@playcastdotio/CoreTypes';
import { ProfileComponent } from '@playcastdotio/SharedComponents';
import constants from '@playcastdotio/GlobalConfig';
import { ClerkProfileSettings } from '../../components/ClerkProfileSettings';
import { isElectron } from '../../api';
import { playerNameState } from '../../api/stores/shared';
import { sendAnalytics, PlaycastAnalyticsPageViewed } from '@playcastdotio/Analytics';
import { getPersistentPlayerId } from '../../api/utilities/persistentId';

export const ProfilePage = () => {
  const context = useContext(PlaycastContext);
  const [currentPage, setCurrentPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [joinStatus, setJoinStatus] = useState<PlayerJoinState['state'] | null>(null);
  const lastOpenedMatchRef = useRef<string | null>(null);
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const user = useUser();

  const { openUserProfileSettings } = ClerkProfileSettings();

  useEffect(() => {
    console.log('Loading profile');

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
      console.log('Unloading profile');
    };
  }, []);

  useEffect(() => {
    if (context.stateStores.profileState?.matchFoundId) {
      console.log('Match found', context.stateStores.profileState?.matchFoundId);
    }
    if (context.stateStores.profileState?.matchBroadcastId && context.stateStores.profileState?.matchFoundId) {
      // Only open a new window if this is a different match than the last one we opened
      if (lastOpenedMatchRef.current !== context.stateStores.profileState?.matchFoundId) {
        // Track this match ID as the last one we opened
        lastOpenedMatchRef.current = context.stateStores.profileState?.matchFoundId;

        // Open link in new tab
        const playerLink = constants.PAVILION_PLAYER_BASE_URL;
        window.open(`${playerLink}j/${context.stateStores.profileState?.matchFoundId}`, '_blank');
        console.log('Opened window for match', context.stateStores.profileState?.matchFoundId);
      } else {
        console.log('Window already opened for match', context.stateStores.profileState?.matchFoundId);
      }
    }
  }, [context.stateStores.profileState]);

  useEffect(() => {
    context.targetFunctions.queryProfile();
    context.targetFunctions.queryOwnerAliases();
    context.targetFunctions.queryProfileInvitations();

    // Set up refresh interval for invitations (every 10 minutes)
    refreshIntervalRef.current = setInterval(() => {
      console.log('Refreshing invitations data (10-minute interval)');
      const cursor = currentPage > 0 ? context.stateStores.profileState?.nextCursor : undefined;
      context.targetFunctions.queryProfileInvitations(cursor, 16, searchTerm);
    }, 30 * 1000); // 30 seconds in milliseconds

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
    };
  }, [searchTerm, currentPage]);

  const onEdit = (profile: Profile) => {
    console.log('Editing profile', profile);
    context.targetFunctions.updateProfile(profile);
  };

  const handlePageForward = () => {
    if (!context.stateStores.profileState?.nextCursor) return;

    setCurrentPage(currentPage + 1);
    context.targetFunctions.queryProfileInvitations(context.stateStores.profileState?.nextCursor, 16, searchTerm);
  };

  const handlePageBack = () => {
    if (currentPage <= 0) return;

    // Reset to first page
    setCurrentPage(0);
    context.targetFunctions.queryProfileInvitations(undefined, 16, searchTerm);
  };

  const handleSearch = (term: string) => {
    setSearchTerm(term);
    setCurrentPage(0);
    context.targetFunctions.queryProfileInvitations(undefined, 16, term);
  };

  const handleCreateMatchmakerLobby = () => {
    console.log('Creating matchmaker lobby');
    context.targetFunctions.profileCreateMatchmakerLobby();
  };

  const handleJoinMatchmakerLobby = (name?: string) => {
    if (name) {
      playerNameState.updateState({ name });
      localStorage.setItem('name', name);
    }
    console.log('Joining matchmaker lobby');
    context.targetFunctions.profileJoinMatchmakerLobby();
  };

  const handleLeaveMatchmakerLobby = () => {
    console.log('Leaving matchmaker lobby');
    context.targetFunctions.profileLeaveMatchmakerLobby();
  };

  const handleStartMatchmakerLobby = (useSteamGuestLogin?: boolean, regionOverride?: string) => {
    console.log('Starting matchmaker lobby', useSteamGuestLogin, regionOverride);
    context.targetFunctions.profileStartMatchmakerLobby(useSteamGuestLogin, regionOverride);
  };

  const handleDestroyMatchmakerLobby = (selectedInvitationId?: string) => {
    console.log('Destroying matchmaker lobby', selectedInvitationId);
    context.targetFunctions.profileDestroyMatchmakerLobby(selectedInvitationId);
  };

  const handleSelectInvitation = (invitationId?: string) => {
    console.log('Selecting invitation', invitationId);
    context.targetFunctions.profileSelectInvitation(invitationId);
  };

  const handleSettingsCogClick = () => {
    console.log('Settings cog clicked');
    openUserProfileSettings();
  };

  const handleKickPlayer = (playerId: string) => {
    console.log('Kicking player', playerId);
    context.targetFunctions.matchmakerKickPlayer(playerId);
  };

  const handleSendEmail = (invitationId: string, target: string, message: string) => {
    console.log('Sending email', invitationId, target, message);
    context.targetFunctions.sendInvitationEmail(target, message, invitationId);
  };

  const handleSendSms = (invitationId: string, target: string, message: string) => {
    console.log('Sending SMS', invitationId, target, message);
    context.targetFunctions.sendInvitationSms(target, message, invitationId);
  };

  const handleTwitchBot = (invitationId: string) => {
    console.log('Sending Twitch bot', invitationId);
    context.targetFunctions.sendTwitchBot(invitationId);
  };

  const handleDiscordBot = (invitationId: string) => {
    console.log('Sending Discord bot', invitationId);
    context.targetFunctions.sendDiscordBot(invitationId);
  };

  const handleRemoveSelectedInvitation = () => {
    console.log('Removing selected invitation');
    context.targetFunctions.profileSelectInvitation(undefined);
  };

  const handleUpdatePlayerName = (name: string) => {
    console.log('Updating player name', name);
    playerNameState.updateState({ name: name });
  };

  return (
    <ProfileComponent
      state={context.stateStores.profileState}
      authToken={context.stateStores.authState.accessToken}
      onEdit={onEdit}
      alias={''}
      ownerAliases={context.stateStores.ownerAliasState?.aliases || {}}
      ownerUsernames={context.stateStores.ownerAliasState?.usernames || {}}
      ownedLobbies={context.stateStores.userEventsState?.ownedLobbies || undefined}
      invitations={context.stateStores.profileState?.invitations || []}
      invitationsLoading={context.stateStores.profileState?.invitationsLoading || false}
      invitationsError={context.stateStores.profileState?.invitationsError || null}
      onSettingsCogClick={handleSettingsCogClick}
      onPageForward={handlePageForward}
      onPageBack={handlePageBack}
      currentPage={currentPage}
      hasNextPage={!!context.stateStores.profileState?.nextCursor}
      onSearch={handleSearch}
      authId={user.user?.id || ''}
      onCreateMatchmakerLobby={handleCreateMatchmakerLobby}
      onJoinMatchmakerLobby={handleJoinMatchmakerLobby}
      onLeaveMatchmakerLobby={handleLeaveMatchmakerLobby}
      onSelectInvitation={handleSelectInvitation}
      onStartMatchmakerLobby={handleStartMatchmakerLobby}
      onDestroyMatchmakerLobby={handleDestroyMatchmakerLobby}
      joinStatus={joinStatus}
      onKickPlayer={handleKickPlayer}
      handleSendEmail={handleSendEmail}
      handleSendSms={handleSendSms}
      invitationSendState={context.stateStores.invitationSendState}
      handleTwitchBot={handleTwitchBot}
      handleDiscordBot={handleDiscordBot}
      onRemoveSelectedInvitation={handleRemoveSelectedInvitation}
      onUpdatePlayerName={handleUpdatePlayerName}
      persistentId={getPersistentPlayerId()}
      parentType='electron'
    />
  );
};
